import React, { ReactElement, useEffect, useState } from 'react';
import {
  Checkbox,
  CheckboxGroup,
  Col,
  DatePicker,
  Details,
  FormGroup,
  Input,
  InsetText,
  Label,
  Loader,
  Row,
  Select,
  Text,
  Textarea,
} from 'idsk-react-components';
import { MultiSelect } from 'slovlex-idsk-react-components';
import { SafeParameterSetter } from '../../../../types/SafeParameterSetter';
import MpkRequest from '../../../../model/pracovnyKrok/MpkRequest';
import LegislativnyMaterialDetail
  from '../../../../model/legislativnyMaterial/LegislativnyMaterialDetail';
import mapDate from '../../../../function/mapDate';
import {
  datePlusDays,
  getDurations,
  getInitialConfigDuration,
  includeSviatkyInEndDate,
  isAfter18Hours,
  isBefore,
  isSviatok,
  isToday,
} from '../../../../function/pripomienkovanieFunctions';
import './mpkStartForm.scss';
import useSviatky from '../../../../hooks/useSviatky';
import Organizacia from '../../../../model/ciselniky/Organizacia';
import useSearchOrganizacie from '../../../../hooks/useSearchOrganizacie';
import {
  MAX_MULTISELECT_RESULT,
  SHOW_ALL_MAX_RESULT,
} from '../../../../constants/MultiSelectConstant';

interface Props {
  materialDetail: LegislativnyMaterialDetail;
  mpkRequest: MpkRequest;
  onUpdateField: SafeParameterSetter<MpkRequest>;
  pripomienkovatelList: Organizacia[];
  pripomienkovatelListLoading: boolean;
  getFieldErrorMessage: (fieldName: string) => string;
  config?: boolean;
  admin?: boolean;
}

const MpkStartForm = ({
  materialDetail,
  mpkRequest,
  onUpdateField,
  pripomienkovatelList,
  pripomienkovatelListLoading,
  getFieldErrorMessage,
  config = false,
  admin = false,
}: Props): ReactElement => {
  const {
    sviatky,
    sviatkyStatus,
  } = useSviatky();
  const [searchText, setSearchText] = useState('');
  const [limit, setLimit] = useState(MAX_MULTISELECT_RESULT);
  const {
    organizacie: dalsiPripomienkovatelList,
    organizacieStatus: dalsiPripomienkovatelListStatus,
  } = useSearchOrganizacie({
    typ: undefined,
    ids: [],
    ldap: true,
    legislativna: undefined,
    platna: true,
    nazov: searchText,
    limit,
  });
  const [durationsDays, setDurationsDays] = useState<Map<number, string>>(new Map());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(updateEndDate, [mpkRequest.durationDays, mpkRequest.startDate, sviatky]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(calculateDurations, [materialDetail, mpkRequest.skratene]);
  useEffect(() => {
    if ((config) && sviatky && !mpkRequest.durationDays) {
      onUpdateField('durationDays', getInitialConfigDuration(
        materialDetail.datumZaciatkuPkMpk,
        materialDetail.datumUkonceniaPkMpk,
        sviatky,
      ) - (mpkRequest.skratene && isNotAfterTodayAndIsAfter18HoursAndIsNotSviatok() ? 1 : 0));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sviatky]);

  function isNotAfterTodayAndIsAfter18HoursAndIsNotSviatok(): boolean {
    return (isToday(mpkRequest.startDate) || isBefore(mpkRequest.startDate))
        && isAfter18Hours()
        && !isSviatok(mpkRequest.startDate, sviatky);
  }

  function updateEndDate(): void {
    if ((config || admin) && sviatky && !mpkRequest.durationDays) {
      onUpdateField('endDate', undefined);
      return;
    }
    if (mpkRequest.skratene) {
      const daysToAdd = isNotAfterTodayAndIsAfter18HoursAndIsNotSviatok()
        ? mpkRequest.durationDays : mpkRequest.durationDays - 1;
      let newEndDate = datePlusDays(mpkRequest.startDate, daysToAdd);

      newEndDate = includeSviatkyInEndDate(mpkRequest.startDate, newEndDate, sviatky);

      onUpdateField('endDate', newEndDate);
    } else {
      const daysToAdd = mpkRequest.durationDays - 1;
      let newEndDate = datePlusDays(mpkRequest.startDate, daysToAdd);

      newEndDate = includeSviatkyInEndDate(mpkRequest.startDate, newEndDate, sviatky);

      onUpdateField('endDate', newEndDate);
    }
  }

  function calculateDurations(): void {
    if ((config || admin) && !mpkRequest.durationDays) {
      return;
    }
    setDurationsDays(getDurations(materialDetail.sablonaProcesu, mpkRequest.skratene));
    if (mpkRequest.skratene) {
      onUpdateField('durationDays', materialDetail.sablonaProcesu.lehotaSkratenaPrednastavenaHodnota);
    } else {
      onUpdateField('durationDays', materialDetail.sablonaProcesu.lehotaPrednastavenaHodnota);
    }
  }

  const updateSkrateneMpk = async (checked: boolean): Promise<void> => {
    onUpdateField('skratene', checked);
    onUpdateField('dovodSkratenia', undefined);
  };

  let dobaTrvaniaPkInput;
  if (config || admin) {
    dobaTrvaniaPkInput = (
      <Input
        type="text"
        id="durationDays"
        width="full"
        autoComplete="off"
        maxLength={4}
        value={mpkRequest.durationDays || ''}
        error={getFieldErrorMessage('durationDays')}
        onChange={(e) => {
          const inputValue = e.target.value;
          const numericValue = parseInt(inputValue, 10);
          if (!Number.isNaN(numericValue)) {
            onUpdateField('durationDays', numericValue);
          }
          if (inputValue === '') {
            onUpdateField('durationDays', inputValue);
          }
        }}
      />
    );
  } else {
    dobaTrvaniaPkInput = (
      <Select
        id="durationDays"
        width="full"
        value={mpkRequest.durationDays}
        onChange={e => onUpdateField('durationDays', e.target.value)}
      >
        {Array.from(durationsDays)
          .map(([numberOfDays, readableText]) => (
            <option key={numberOfDays} value={numberOfDays}>{readableText}</option>
          ))}
      </Select>
    );
  }

  return materialDetail && (
  <div className="odoslat-do-mpk-form">
    <Row>
      <Col widthDesktop="one-third">
        <FormGroup
          className="start-date-form-group"
          error={!!getFieldErrorMessage('startDate')}
        >
          <Label htmlFor="zmenaOd">Dátum začiatku PK</Label>
          <DatePicker
            name="startDate"
            width="full"
            showIcon
            minDate={config ? undefined : new Date()}
            maxDate={config ? new Date() : undefined}
            value={mpkRequest.startDate}
            onChange={date => onUpdateField('startDate', date)}
            error={getFieldErrorMessage('startDate')}
          />
        </FormGroup>
      </Col>
    </Row>
    <Row>
      <Col widthDesktop="one-third">
        {materialDetail.sablonaProcesu?.lehotaSkratenaPrednastavenaHodnota > 0 && (
        <FormGroup>
          <CheckboxGroup small>
            <Checkbox
              checked={mpkRequest.skratene}
              id="is-skratene-mpk"
              className="is-skratene-mpk__checkbox"
              onChange={e => updateSkrateneMpk(e.target.checked)}
            >Skrátené PK
            </Checkbox>
          </CheckboxGroup>
        </FormGroup>
        )}
      </Col>
    </Row>
    {isSviatok(mpkRequest.startDate, sviatky) && (
    <InsetText
      id="startDateWarning"
      type="warn"
      className="datum-zaciatku-warning__inset-text"
    >
      <Text type="span" bold>Dátum začiatku štádia Pripomienkové konanie
        ste nakonfigurovali na deň pracovného pokoja. Tento dátum bude viditeľný v
        detaile legislatívneho procesu ako dátum začiatku PK. Lehota sa začne počítať od
        nasledujúceho pracovného
        dňa.
      </Text>
    </InsetText>
    )}
    <Row>
      <Col widthDesktop="one-half">
        {mpkRequest.skratene && (
        <FormGroup
          error={!!getFieldErrorMessage('dovodSkratenia')}
        >
          <Label htmlFor="dovodSkratenia">Dôvod skrátenia PK</Label>
          <Textarea
            showRemaining
            width="full"
            name="dovodSkratenia"
            expandHeight
            maxLength={2048}
            value={mpkRequest.dovodSkratenia}
            onChange={({ target }) => onUpdateField('dovodSkratenia', target.value)}
            error={getFieldErrorMessage('dovodSkratenia')}
          />
        </FormGroup>
        )}
      </Col>
    </Row>
    <Row>
      <Col widthDesktop="one-third">
        <FormGroup error={!!getFieldErrorMessage('durationDays')}>
          <Label htmlFor="durationDays">Doba trvania PK (pracovných dní)</Label>
          {dobaTrvaniaPkInput}
        </FormGroup>
      </Col>
    </Row>

    <Row>
      <Col widthDesktop="one-third">
        <Loader loading={sviatkyStatus.loading}>
          <FormGroup>
            <Label htmlFor="endDate">Dátum ukončenia PK (vrátane)</Label>
            <span className="idsk-hint">Dátum ukončenia PK bude vyrátaný automaticky po zadaní doby trvania PK.</span>
            <Input
              id="endDate"
              type="text"
              width="full"
              disabled
              value={mapDate(mpkRequest.endDate) || ''}
            />
          </FormGroup>
        </Loader>
      </Col>
    </Row>
    <Details summary="Povinní pripomienkovatelia">
      <Loader loading={pripomienkovatelListLoading}>
        {pripomienkovatelList && (
        <ul className="povinni-pripomienkovatelia-ul">
          {pripomienkovatelList?.map(pripomienkovatel => (
            <li key={pripomienkovatel.uuid}>{pripomienkovatel.nazov}</li>
          ))}
        </ul>
        )}
      </Loader>
    </Details>
    { materialDetail?.typ?.kod !== 'PoslaneckyNavrhZakon' && (
    <Row>
      <Col widthDesktop="one-half">
        <MultiSelect
          loading={dalsiPripomienkovatelListStatus.loading}
          handleShowAll={() => {
            setLimit(SHOW_ALL_MAX_RESULT);
          }}
          withShowAllButton={(limit === MAX_MULTISELECT_RESULT
                        && dalsiPripomienkovatelList.length === MAX_MULTISELECT_RESULT)
                    || (limit === SHOW_ALL_MAX_RESULT && dalsiPripomienkovatelList.length <= MAX_MULTISELECT_RESULT)}
          searchText={searchText}
          setSearchText={(text) => {
            setSearchText(text);
            setLimit(MAX_MULTISELECT_RESULT);
          }}
          label="Ďalší pripomienkovatelia"
          itemListAttribute="nazov"
          width="full"
          data={dalsiPripomienkovatelList?.filter(
            dalsiPripomienkovatel => !pripomienkovatelList?.map(x => x.uuid)
              .includes(dalsiPripomienkovatel.uuid),
          )}
          selectedItemList={mpkRequest.dalsiPripomienkovatelia}
          dataAttributesToShow={[{
            key: 'nazov',
            label: '',
          }]}
          visibilityMinSearchLength={0}
          setSelectedItemList={(newData) => {
            onUpdateField('dalsiPripomienkovatelia', newData);
          }}
          maxResults={SHOW_ALL_MAX_RESULT}
        />
      </Col>
    </Row>
    )}
    {!config && (
    <InsetText type="warn">
      <Text type="span" bold>V prípade, že v pracovný deň bude proces odoslaný do PK po
        18:00
        hodine a je zvolená
        možnosť skrátené PK, materiál bude na pripomienkovanie dostupný ihneď, ale lehota
        ukončenia sa bude počítať akoby PK začalo až nasledujúci pracovný deň.
      </Text>
    </InsetText>
    )}
  </div>
  );
};

export default MpkStartForm;
